import { without } from "../fields";
import { wallMax } from "../land/cities";
import type { FactionId, HexState, RunState } from "../types/state";
import { findHex } from "./board";
import { kingOf } from "./pools";
import { hostile, sameOwner } from "./sides";

/**
 * The fall of a capital (K7, ADR-0012 W4, `rules.capitalFall`): a kingdom whose capital a rival
 * holds collapses, so a war of many kingdoms ends by its capitals, not by hunting the last unit of
 * each. The conqueror keeps what it took; the rest of the fallen kingdom is nobody's.
 */

/** The kingdom's capital, its King's starting city, is held by a rival: never in a run without a King. */
export function capitalFallen(state: RunState, faction: FactionId): boolean {
  const king = kingOf(state, faction);
  const capital = king && findHex(state, king.capital);
  return capital !== undefined && hostile(state.rules, capital.owner, faction);
}

/**
 * A hex of a fallen kingdom goes to nobody as a hex changes hands (`capture`): a build in progress
 * is lost, the mayor and his lord's leader go, and so do City Command's orders; its walls are at
 * most a neutral hex's.
 */
function released(hex: HexState, state: RunState): HexState {
  const unfinished = hex.structureReadyTick !== 0;
  const neutral: HexState = {
    ...without(hex, "stance", "leader", "makes", "arrow", "arrowTick"),
    owner: "none",
    structure: unfinished ? "none" : hex.structure,
    structureReadyTick: 0,
    unitBuild: "none",
    unitBuildReadyTick: 0,
    mayor: false,
  };
  const walls = Math.min(hex.walls, wallMax(neutral, state.rules, state.tick));
  return walls === neutral.walls ? neutral : { ...neutral, walls };
}

/** The kingdom collapses: its units disband, its King's court is gone and its land is nobody's. */
export function collapse(state: RunState, faction: FactionId): RunState {
  return {
    ...state,
    hexes: state.hexes.map((hex) => (sameOwner(hex.owner, faction) ? released(hex, state) : hex)),
    units: state.units.filter((unit) => !sameOwner(unit.owner, faction)),
    kings: state.kings && without(state.kings, faction),
  };
}

/**
 * The field's step after the moves and the courts' flight (K7): every kingdom whose capital has
 * fallen collapses, in the run's order, before contact and blows, so it strikes none after it.
 */
export function fallOfCapitals(state: RunState): RunState {
  if (!state.rules.capitalFall) {
    return state;
  }
  return state.factions.reduce(
    (next, faction) => (capitalFallen(next, faction) ? collapse(next, faction) : next),
    state,
  );
}
